import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChalkLogoMark, ChalkLogoLockup, ChalkWordmark } from "./Logo";
import { SHOW_TOURNAMENT_MARKETING } from "../../lib/featureFlags";

// Single brand-token map reused across all marketing + legal pages so the
// design stays uniform without importing the full Landing palette.
export const LEGAL_C = {
  board: "#1a3a2a",
  white: "rgba(255,255,255,0.92)",
  green: "#2E7D52",
  greenChalk: "#4db87a",
  cream: "#f5f0e8",
  offWhite: "#faf8f4",
  border: "rgba(0,0,0,0.08)",
  muted: "#5a5a5a",
  textDark: "#1a1a1a",
};
export const LEGAL_SERIF = "'Bebas Neue', Oswald, Impact, sans-serif";
export const LEGAL_BODY  = "'Barlow', system-ui, sans-serif";
export const LEGAL_MARKER = "'Permanent Marker', Caveat, cursive";

export const LEGAL_LINKS = [
  { to: "/terms", label: "Terms" },
  { to: "/privacy", label: "Privacy" },
  { to: "/children-privacy", label: "Children's Privacy" },
  { to: "/refund-policy", label: "Refund Policy" },
  { to: "/community-policy", label: "Community Policy" },
  { to: "/ai-disclaimer", label: "AI Disclaimer" },
  { to: "/delete-data", label: "Delete My Data" },
];

/**
 * Wrap a static legal page with the Chalk OS brand chrome:
 * top nav, page title, document.title sync, accessible skip-link,
 * and the shared LegalFooter (with all 7 policy links).
 *
 * Pages compose by rendering `LegalSection` children inside `LegalLayout`.
 */
export default function LegalLayout({ title, eyebrow, lastUpdated, children, testId }) {
  useEffect(() => {
    const prev = document.title;
    document.title = `${title} — ChalkOS`;
    return () => { document.title = prev; };
  }, [title]);

  return (
    <div
      data-testid={testId || "legal-page"}
      className="chalk-min-h-screen"
      style={{
        fontFamily: LEGAL_BODY,
        color: LEGAL_C.textDark, background: LEGAL_C.cream,
      }}
    >
      <a href="#main" className="chalk-skip-link" data-testid="skip-link">Skip to main content</a>
      <LegalTopBar />
      <main id="main" tabIndex={-1} style={{ padding: "44px 22px 72px", maxWidth: 820, margin: "0 auto" }}>
        {eyebrow && (
          <div style={{
            fontFamily: LEGAL_BODY, fontWeight: 700,
            fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
            color: LEGAL_C.green, marginBottom: 10,
          }}>{eyebrow}</div>
        )}
        <h1 style={{
          fontFamily: LEGAL_SERIF, fontWeight: 400,
          fontSize: "clamp(2.6rem, 5vw, 4rem)",
          lineHeight: 0.95, letterSpacing: "0.02em",
          textTransform: "uppercase", margin: 0,
        }}>{title}</h1>
        <span style={{ display: "block", width: 120, height: 3, background: LEGAL_C.green, margin: "16px 0 0", borderRadius: 2 }} />
        {lastUpdated && (
          <div style={{ marginTop: 16, fontSize: 13, color: LEGAL_C.muted, fontFamily: LEGAL_BODY }}>
            Last updated: {lastUpdated}
          </div>
        )}
        <div style={{ marginTop: 36, display: "flex", flexDirection: "column", gap: 28 }}>
          {children}
        </div>
        <LegalContactBlock />
      </main>
      <LegalFooter />
    </div>
  );
}

function LegalTopBar() {
  return (
    <header style={{
      padding: "16px 22px", borderBottom: `1px solid ${LEGAL_C.border}`,
      background: LEGAL_C.offWhite,
    }}>
      <div style={{
        maxWidth: 1200, margin: "0 auto", display: "flex",
        justifyContent: "space-between", alignItems: "center", gap: 16,
      }}>
        <Link to="/" style={{ textDecoration: "none" }} aria-label="Chalk OS home">
          <ChalkLogoLockup size={40} color={LEGAL_C.textDark} />
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Link to="/pricing" data-testid="legal-nav-pricing" style={navLinkStyle}>Pricing</Link>
          <Link to="/login" data-testid="legal-nav-signin" style={navLinkStyle}>Sign in</Link>
        </div>
      </div>
    </header>
  );
}

const navLinkStyle = {
  fontFamily: LEGAL_BODY, fontWeight: 700, fontSize: 13,
  letterSpacing: "0.1em", textTransform: "uppercase",
  color: LEGAL_C.textDark, textDecoration: "none", padding: "10px 14px",
};

/**
 * Section heading + body wrapper used inside legal pages. Heading uses the
 * brand serif; body uses sans-serif with comfortable reading line-height.
 */
export function LegalSection({ heading, children, testId }) {
  return (
    <section data-testid={testId} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {heading && (
        <h2 style={{
          fontFamily: LEGAL_SERIF, fontWeight: 400,
          fontSize: 28, letterSpacing: "0.03em", textTransform: "uppercase",
          lineHeight: 1.05, margin: 0, color: LEGAL_C.textDark,
        }}>{heading}</h2>
      )}
      <div style={{ fontSize: 15, lineHeight: 1.65, color: LEGAL_C.textDark, fontFamily: LEGAL_BODY }}>{children}</div>
    </section>
  );
}

/**
 * Bulleted list rendered with brand-consistent spacing + spaced bullets.
 * Caller passes an array of strings or JSX nodes.
 */
export function LegalList({ items, testId }) {
  return (
    <ul data-testid={testId} style={{ paddingLeft: 22, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ fontSize: 15, lineHeight: 1.65 }}>{it}</li>
      ))}
    </ul>
  );
}

function LegalContactBlock() {
  return (
    <div style={{
      marginTop: 40, padding: 22, borderRadius: 14,
      background: LEGAL_C.offWhite, border: `1px solid ${LEGAL_C.border}`,
      borderLeft: `2px solid ${LEGAL_C.green}`,
    }}>
      <div style={{
        fontFamily: LEGAL_BODY, fontWeight: 700, fontSize: 11,
        color: LEGAL_C.greenChalk, textTransform: "uppercase", letterSpacing: "0.14em",
      }}>
        Questions?
      </div>
      <div style={{ marginTop: 8, fontSize: 15, color: LEGAL_C.textDark, fontFamily: LEGAL_BODY }}>
        Email <a href="mailto:support@getchalkos.com" style={{ color: LEGAL_C.green, fontWeight: 700 }}>support@getchalkos.com</a>.
        Include your name, team name, and the request you&apos;d like to make.
      </div>
    </div>
  );
}

/**
 * Reusable footer for legal + marketing pages. Lists every legal link
 * plus support email + product nav.
 */
export function LegalFooter() {
  return (
    <footer
      data-testid="legal-footer"
      style={{
        padding: "36px 22px 64px",
        background: LEGAL_C.board,
        color: LEGAL_C.white,
      }}
    >
      <div style={{
        maxWidth: 1200, margin: "0 auto",
        display: "grid", gap: 28,
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
        alignItems: "start",
      }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <ChalkLogoMark size={36} />
            <ChalkWordmark size={22} color={LEGAL_C.white} />
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: "rgba(245,240,232,0.65)", fontFamily: LEGAL_BODY, lineHeight: 1.5 }}>
            © {new Date().getFullYear()} Chalk OS.<br />
            Youth sports coordination platform.
          </div>
          <a
            href="mailto:support@getchalkos.com"
            data-testid="footer-support-email"
            style={{ display: "inline-block", marginTop: 14, color: "rgba(245,240,232,0.85)", fontSize: 13, textDecoration: "none", fontFamily: LEGAL_BODY }}
          >
            support@getchalkos.com
          </a>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 22px" }}>
          <FooterCol heading="Product">
            <FooterLink to="/" testId="footer-link-home">Home</FooterLink>
            <FooterLink to="/coaches" testId="footer-link-coaches">For coaches</FooterLink>
            <FooterLink to="/parents" testId="footer-link-parents">For parents</FooterLink>
            <FooterLink to="/team-staff" testId="footer-link-team-staff">For team staff</FooterLink>
            <FooterLink to="/leagues" testId="footer-link-leagues">For leagues</FooterLink>
            <FooterLink to="/pricing" testId="footer-link-pricing">Pricing</FooterLink>
            {SHOW_TOURNAMENT_MARKETING && <FooterLink to="/tournaments" testId="footer-link-tournaments">Tournaments</FooterLink>}
            <FooterLink to="/login" testId="footer-link-signin">Sign in</FooterLink>
          </FooterCol>
          <FooterCol heading="Legal">
            {LEGAL_LINKS.map((l) => (
              <FooterLink key={l.to} to={l.to} testId={`footer-link-${l.to.slice(1)}`}>{l.label}</FooterLink>
            ))}
          </FooterCol>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ heading, children }) {
  return (
    <div>
      <div style={{
        fontFamily: LEGAL_BODY, fontWeight: 700,
        fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase",
        color: "rgba(245,240,232,0.55)", marginBottom: 12,
      }}>{heading}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

function FooterLink({ to, children, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      style={{ color: "rgba(245,240,232,0.85)", fontSize: 13, textDecoration: "none", fontFamily: LEGAL_BODY }}
    >
      {children}
    </Link>
  );
}
